import React, { Component } from 'react';
import { loadScript } from '@ecs/tng-common/scripts/helpers';
import IconsHolder from '@ecs/tng-common/components/icons-holder/IconsHolder';
import { QSWidgetClassNames, SMWidgetTypes } from '../../helpers/interfaces';
import parse from 'html-react-parser';

interface SMWidgetProps {
  smWidget: SMWidgetTypes;
  className?: QSWidgetClassNames;
  postData: {
    id: string;
    catSlug: string;
    catId: string;
    isSEMtemplate: boolean;
  };
}
interface IWidgetData {
  title: string;
  description: string;
  type: string;
  offerId: string;
  fileId: string;
}
interface SMWidgetTypeState {
  scriptLoaded: boolean;
  isError: boolean;
  trackerSrc?: string;
}

interface IError {
  icon: string;
  subcopy: string;
}

const widgetConfiguration: Record<SMWidgetTypes, IWidgetData> = {
  mortgage: {
    title: 'Compare mortgage rates',
    description:
      'Check today’s rates to find the best loan offers. Staying updated on current rates helps you secure a competitive mortgage and save more over time.',
    type: 'sm-mortgage',
    offerId: '1685',
    fileId: '670'
  },
  mortgagerefi: {
    title: 'Compare mortgage refinance rates',
    description:
      'Check today’s refinance offers and current rates to find the right loan to lower payments or shorten your term.',
    type: 'sm-mortgagerefi',
    offerId: '1684',
    fileId: '671'
  },
  heloc: {
    title: 'Compare HELOC rates',
    description:
      'Check today’s HELOC offers and current rates to find the right line of credit for accessing your home’s equity.',
    type: 'sm-heloc',
    offerId: '1719',
    fileId: '657'
  },
  heloan: {
    title: 'Compare home equity loans',
    description:
      'Check today’s home equity loan offers and current rates to unlock your home’s value with a predictable monthly payment.',
    type: 'sm-heloan',
    offerId: '1720',
    fileId: '658'
  }
};

const error: IError = {
  icon: 'ecs-font-icon-con-chat-bubble',
  subcopy:
    'Looks like we’re having some trouble showing offers from our partners right now. In the meantime, you can sign in or create a free <a href="/help/login.html">Experian account</a> to unlock tools that can help you reach your financial goals.'
};
class SMWidget extends Component<SMWidgetProps, SMWidgetTypeState> {
  private widget: IWidgetData = widgetConfiguration[this.props.smWidget];
  private errorTimeout?: ReturnType<typeof setTimeout>;

  constructor(props: SMWidgetProps) {
    super(props);
    this.state = {
      scriptLoaded: false,
      isError: false
    };
  }

  componentDidMount() {
    if (typeof window !== 'undefined' && this.widget) {
      this.initializeWidget();
      this.errorTimeout = setTimeout(() => {
        if (!this.state.scriptLoaded) {
          this.setState({ isError: true });
        }
      }, 2000);
    }
  }

  componentWillUnmount() {
    if (this.errorTimeout) clearTimeout(this.errorTimeout);
    // Cleanup scripts on unmount
    const el = document.getElementById('smEmbedHelper');
    if (el) {
      document?.head?.removeChild(el);
    }
  }

  getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return '';
  };

  getQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      mkid: params.get('mkid') || ''
    };
  };

  toApplyUITreatment = () => {
    const { isSEMtemplate, catSlug } = this.props.postData;
    const { className } = this.props;
    let toApplyUITreatment: boolean = true;

    if (isSEMtemplate || catSlug === 'lp') {
      toApplyUITreatment = false;
    }
    if (className === 'qs-widget-background') {
      toApplyUITreatment = true;
    }
    if (toApplyUITreatment && className === 'qs-widget-no-background') {
      toApplyUITreatment = false;
    }
    return toApplyUITreatment;
  };

  initializeWidget() {
    const queryParams = this.getQueryParams();
    const mkid =
      queryParams.mkid || this.getCookie('marketingid') || '00000000-0000-0000-0000-000000000000';
    let counter = 0;
    const int = setInterval(() => {
      if (window.ECS?.tms?.data) {
        clearInterval(int);
        const affSub = {
          id: this.props.postData.id ? `id:${this.props.postData.id}` : '',
          cat: this.props.postData.catSlug ? `|cat:${this.props.postData.catSlug}` : '',
          pc: window.ECS?.tms?.data.pc ? `|pc:${window.ECS?.tms?.data.pc}` : '',
          cc: window.ECS?.tms?.data.cc ? `|cc:${window.ECS?.tms?.data.cc}` : '',
          mkid: `|mkid:${mkid}`
        };

        const affSubEncoded = encodeURIComponent(
          `${affSub.id}${affSub.cat}${affSub.pc}${affSub.cc}${affSub.mkid}`
        );
        const trackLink = `https://track.supermoney.com/aff_c?offer_id=${this.widget.offerId}&aff_id=2597&file_id=${this.widget.fileId}&aff_sub=${affSubEncoded}`;
        window.smOptions = window.smOptions || {
          primaryColor: '#2A5595',
          btnTextColor: '',
          linkTextColor: '#2A5595',
          ratingPositiveColor: '',
          ratingNeutralColor: '',
          ratingNegativeColor: '',
          scores: 'hide'
        };
        window.iframeSrc = window.iframeSrc || [];
        window.iframeSrc.push({
          src: trackLink
        });
        this.setState({ trackerSrc: trackLink });
        this.load();
      } else if (counter > 15) {
        clearInterval(int);
      }
      counter++;
    }, 100);
  }

  load() {
    const scriptSrc = `${process.env.SUPERMONEY_HOST}`;
    const scriptId = 'smEmbedHelper';
    loadScript(scriptSrc, scriptId)
      .then(() => {
        if (window.sm_embed_init && typeof window.sm_embed_init === 'function') {
          window.sm_embed_init();
          this.setState({ scriptLoaded: true, isError: false });
        }
      })
      .catch((error) => {
        console.error(error);
        this.setState({ isError: true });
      });
  }

  renderWidgetError() {
    return (
      <div className="card border bg-alert">
        <IconsHolder
          icon={error.icon}
          size="lg"
          className="bg-yellow text-orange bg-opacity-30 me-3"
        />
        <p className="mb-0">{parse(error.subcopy)}</p>
      </div>
    );
  }

  render() {
    if (!this.widget) return null;
    const toApplyUITreatment = this.toApplyUITreatment();
    return (
      <div
        className={`${toApplyUITreatment ? 'bg-light mb-4 mb-lg-5 rounded px-3 px-lg-4 pb-lg-2 pt-4 pt-lg-6' : ''}${this.state.isError ? ' pb-4 pb-lg-6' : ''}`}>
        {toApplyUITreatment ? (
          <>
            <h3 className="mb-3">{this.widget.title}</h3>
            <p>{this.widget.description}</p>
          </>
        ) : null}
        {!this.state.scriptLoaded && this.state.isError ? (
          this.renderWidgetError()
        ) : (
          <>
            <div>
              <iframe className="sm_widget"></iframe>
              <img
                className="sm_widget_tracker"
                width="0"
                height="0"
                alt=""
                src={this.state.trackerSrc || ''}
              />
            </div>
          </>
        )}
      </div>
    );
  }
}

export default SMWidget;
